import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, AlertTriangle } from "lucide-react";
import { seoHead } from "@/lib/seo";
import { ResourceShareBlock } from "@/components/ResourceShareBlock";
import { CTABlock } from "@/components/CTABlock";
import { useBusinessConfig } from "@/lib/use-business-config";

const CATEGORIES: { title: string; items: string[] }[] = [
  {
    title: "1. Lights and indicators",
    items: [
      "All headlights work on dipped and main beam",
      "Sidelights, brake lights and reverse lights work",
      "All indicators and hazard lights flash correctly",
      "Number plate lights work",
      "No cracked or discoloured lenses",
    ],
  },
  {
    title: "2. Tyres and wheels",
    items: [
      "Tread depth ≥ 1.6mm across central 3/4 of all tyres",
      "No cuts, bulges or cord showing",
      "Correct tyre pressures (see door pillar or filler cap)",
      "Locking wheel nut key in the car",
    ],
  },
  {
    title: "3. Windscreen, wipers and washers",
    items: [
      "No chips or cracks in driver's view (Zone A) over 10mm, or 40mm elsewhere",
      "Wipers clear screen with no smearing or split blades",
      "Washer jets work, washer bottle topped up",
    ],
  },
  {
    title: "4. Brakes and handbrake feel",
    items: [
      "Brake pedal firm — no spongy feel",
      "Car pulls up straight under firm braking",
      "Handbrake holds on a slope",
      "No grinding or squealing noises",
    ],
  },
  {
    title: "5. Mirrors and visibility",
    items: [
      "Interior and both door mirrors present and secure",
      "No cracks in mirror glass",
    ],
  },
  {
    title: "6. Number plates",
    items: [
      "Front and rear plates fitted, clean and legible",
      "Correct font, spacing and colour (no novelty plates)",
    ],
  },
  {
    title: "7. Horn",
    items: ["Horn works and is loud enough to be heard"],
  },
  {
    title: "8. Seatbelts",
    items: [
      "All seatbelts present, secure and retract correctly",
      "Buckles lock and release cleanly",
    ],
  },
  {
    title: "9. Dashboard warning lights",
    items: [
      "No red warning lights on after start-up (engine, brake, airbag, ABS)",
      "Engine management light not illuminated",
      "TPMS / tyre pressure light off",
    ],
  },
  {
    title: "10. Fluid leaks",
    items: [
      "No oil, coolant or brake fluid drips under the car",
      "Coolant and oil at correct levels",
    ],
  },
  {
    title: "11. Exhaust smoke and noise",
    items: [
      "Exhaust is secure, no blowing or loud noise",
      "No excessive blue or black smoke",
    ],
  },
  {
    title: "12. Documents and accessories",
    items: [
      "V5C logbook to hand (not required at test, but useful)",
      "Locking wheel nut key in the car",
      "Boot empty enough to reach spare/space-saver if fitted",
    ],
  },
];

export const Route = createFileRoute("/resources/mot-pass-checklist")({
  head: () =>
    seoHead({
      path: "/resources/mot-pass-checklist",
      title: "Free MOT Pass Checklist PDF-Style Guide | Woodseer Autos",
      description:
        "Free printable MOT checklist: lights, tyres, brakes, wipers, washers, horn, warning lights and more. Prepare before your MOT in East London.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Free Resources", path: "/free-car-resources" },
        { name: "MOT Pass Checklist", path: "/resources/mot-pass-checklist" },
      ],
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Prepare your car for its MOT",
          description:
            "Free 12-section MOT pass preparation checklist for UK drivers, written by Woodseer Autos.",
          step: CATEGORIES.map((c) => ({
            "@type": "HowToStep",
            name: c.title,
            itemListElement: c.items.map((t) => ({ "@type": "HowToDirection", text: t })),
          })),
        },
      ],
    }),
  component: MOTChecklistResource,
});

function MOTChecklistResource() {
  const biz = useBusinessConfig();
  return (
    <>
      <section className="bg-foreground text-background py-14 md:py-20 no-print">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Free resource</p>
          <h1 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight">
            MOT Pass Preparation Checklist
          </h1>
          <p className="mt-4 text-base text-background/70 leading-relaxed max-w-2xl">
            This checklist does not guarantee a pass, but it helps you spot common MOT issues before your test.
            Print it, work through it on the driveway, and call us if anything looks wrong.
          </p>
          <a
            href={biz.phoneHref}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <Phone className="h-4 w-4" /> Call {biz.phone}
          </a>
        </div>
      </section>

      <div className="print-only px-6 pt-6 text-foreground">
        <p className="text-xl font-extrabold">Woodseer Autos — MOT Pass Checklist</p>
        <p className="text-sm">{NAP.phone} · woodseerautos.co.uk · 14 Copenhagen Place, London E14 7DF</p>
      </div>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-3xl px-4 md:px-6 space-y-6">
          {CATEGORIES.map((c) => (
            <div key={c.title} className="rounded-xl border border-border bg-card p-5 md:p-6">
              <h2 className="text-base md:text-lg font-bold text-foreground">{c.title}</h2>
              <ul className="mt-3 space-y-2">
                {c.items.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-foreground leading-relaxed">
                    <span
                      aria-hidden
                      className="mt-0.5 inline-block h-4 w-4 shrink-0 rounded-sm border-2 border-foreground/40"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 md:p-6">
            <div className="flex items-center gap-2 text-primary">
              <AlertTriangle className="h-4 w-4" />
              <p className="text-[11px] font-bold tracking-[0.25em] uppercase">Found something worrying?</p>
            </div>
            <p className="mt-2 text-sm text-foreground leading-relaxed">
              Call Woodseer Autos before your MOT on{" "}
              <a href={biz.phoneHref} className="font-bold underline">{biz.phone}</a>. We'll
              advise honestly whether it's worth fixing first or letting the test flag it.
            </p>
            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              <Link to="/mot" className="font-semibold text-primary hover:underline">MOT Testing →</Link>
              <Link to="/book" className="font-semibold text-primary hover:underline">Book a slot →</Link>
              <Link to="/free-car-resources" className="font-semibold text-primary hover:underline">More free resources →</Link>
            </div>
          </div>
        </div>
      </section>

      <ResourceShareBlock path="/resources/mot-pass-checklist" title="MOT Pass Preparation Checklist" />
      <CTABlock headline="Want us to check it for you?" subtext="Call is fastest. Honest pre-MOT inspection in Poplar, E14." />
    </>
  );
}
